import { z } from "zod";

import { Form } from "@/components/ui/form";
import i18n from "@/i18n";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";

import { Diff } from "@/features/FormCard/Diff";

import { DatePickerField, InputField } from "@/features/FormCard/Fields";
import { Tables } from "../../../gen/tableNames/tables";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import { parseIntValue } from "@/features/FormCard/utils";

import { FormActionsUpdate } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";

const FormSchema = z.object({
  date: z.preprocess((val) => parseToDateValue(val), dateSchema).optional(),
  sustainalytics_id: z.preprocess(
    (val) => parseIntValue(val as string),
    z.number().int(),
  ),
  esg_risk_score: z.coerce.number().nullable().optional(),
  environment_risk_score: z.coerce.number().nullable().optional(),
  social_risk_score: z.coerce.number().nullable().optional(),
  governance_risk_score: z.coerce.number().nullable().optional(),
  country_risk_score: z.coerce.number().nullable().optional(),
  carbon_emissions_scope_1: z.coerce.number().nullable().optional(),
  carbon_emissions_scope_2: z.coerce.number().nullable().optional(),
  carbon_emissions_scope_3: z.coerce.number().nullable().optional(),
  evic_sfdr: z.coerce.number().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export function G4SustainAnalyticsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    handleDelete,
  } = useEditForm({
    id,
    client,
    table_name,
    FormSchema,
  });

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {i18n.t(`${Tables.G4_SUSTAINALYTICS_ESG}.edit_title`, {
                  ns: "tables",
                })}{" "}
                {id}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="date"
                    label={i18n.t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.date`,
                      {
                        ns: "tables",
                      },
                    )}
                    control={form.control}
                  />
                  <InputField
                    name="sustainalytics_id"
                    label={i18n.t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.sustainalytics_id`,
                      {
                        ns: "tables",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField
                    name="esg_risk_score"
                    label={i18n.t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.esg_risk_score`,
                      {
                        ns: "tables",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="environment_risk_score"
                    label={i18n.t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.environment_risk_score`,
                      {
                        ns: "tables",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField
                    name="social_risk_score"
                    label={i18n.t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.social_risk_score`,
                      {
                        ns: "tables",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="governance_risk_score"
                    label={i18n.t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.governance_risk_score`,
                      {
                        ns: "tables",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField
                    name="country_risk_score"
                    label={i18n.t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.country_risk_score`,
                      {
                        ns: "tables",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="evic_sfdr"
                    label={i18n.t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.evic_sfdr`,
                      {
                        ns: "tables",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField
                    name="carbon_emissions_scope_1"
                    label={i18n.t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.carbon_emissions_scope_1`,
                      {
                        ns: "tables",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="carbon_emissions_scope_2"
                    label={i18n.t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.carbon_emissions_scope_2`,
                      {
                        ns: "tables",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField
                    name="carbon_emissions_scope_3"
                    label={i18n.t(
                      `${Tables.G4_SUSTAINALYTICS_ESG}.columns.carbon_emissions_scope_3`,
                      {
                        ns: "tables",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                  <div />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={i18n.t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, [], {}, table_name)
              : i18n.t("no_changes")}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
